// src/plugins/DeviceIdentifyPlugin.ts
import { IPipelinePlugin } from '../types/plugin';
import { QueuedEvent } from '../types';
import { PluginContext } from '../types/plugin_context';
import { DeviceIdentifyPluginConfig } from '../types/plugins/deviceIdentify';
import { PluginCategory, PluginType } from '../types/plugin_categories';
import { generateUUIDv4 } from '../utils/uuid';
import {} from /* ILogger */ '../utils/logger';

const DEFAULT_LOCAL_STORAGE_KEY_PREFIX = 'insightsDeviceId';

export class DeviceIdentifyPlugin implements IPipelinePlugin {
  public readonly name = 'DeviceIdentifyPlugin';
  public readonly pluginType = PluginType.PIPELINE;
  public readonly category = PluginCategory.ENRICHMENT;
  public readonly version = '1.0.0'; // Example version
  public isActive = false;

  private deviceId?: string;
  private localStorageKey!: string;
  private resolvedConfig?: DeviceIdentifyPluginConfig;
  private context?: PluginContext;

  constructor(config?: DeviceIdentifyPluginConfig) {
    // Store constructor config as fallback, but prefer context.pluginConfig
    this.resolvedConfig = config;
  }

  public initialize(context: PluginContext): void {
    this.context = context;

    if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') {
      this.context?.logger.warn('localStorage is not available. Plugin will not operate');
      return;
    }

    this.resolvedConfig = {
      ...this.resolvedConfig,
      ...(context.pluginConfig as DeviceIdentifyPluginConfig),
    };

    const { clientKey } = context.config;
    if (!clientKey) {
      this.context?.logger.error(
        'clientKey is missing from SDK config. Cannot determine localStorage key. Plugin will not operate effectively.'
      );
      return;
    }

    this.localStorageKey =
      this.resolvedConfig?.localStorageKeyName ??
      `${DEFAULT_LOCAL_STORAGE_KEY_PREFIX}.${clientKey}`;

    try {
      let storedDeviceId = localStorage.getItem(this.localStorageKey);
      if (!storedDeviceId) {
        storedDeviceId = generateUUIDv4();
        localStorage.setItem(this.localStorageKey, storedDeviceId);
        this.context?.logger.debug(
          `New device ID generated and stored: ${storedDeviceId} (Key: ${this.localStorageKey})`
        );
        this.deviceId = storedDeviceId;
      } else {
        this.context?.logger.debug(
          `Existing device ID retrieved: ${storedDeviceId} (Key: ${this.localStorageKey})`
        );
      }
      this.deviceId = storedDeviceId;
    } catch (error) {
      this.context?.logger.error('Error accessing localStorage:', error);
      // Plugin might not function correctly if deviceId cannot be set.
    }
    this.isActive = true;
  }

  public processEvent(event: QueuedEvent): QueuedEvent | null {
    if (!event) {
      // Basic check for null/undefined event
      this.context?.logger.warn('Received null or undefined event object.');
      return null;
    }

    const updatedEvent: QueuedEvent = {
      ...event, // Spread the original event to keep all properties
    };
    this.context?.logger.debug(`Processing event "${event.eventType}" with DeviceIdentifyPlugin.`);

    if (this.deviceId) {
      updatedEvent.deviceId = this.deviceId;
    } else {
      // This might happen if localStorage was unavailable or errored during init.
      this.context?.logger.warn(
        `Device ID not available during event processing for event "${event.eventType}". It will not be added to the event.`
      );
    }

    return updatedEvent;
  }

  public start(): void {
    this.isActive = true;
    this.context?.logger.debug('DeviceTracking Started - now active for event processing.');
  }

  public stop(): void {
    this.isActive = false;
    this.context?.logger.debug('Stopped - no longer processing events.');
  }

  public destroy(): void {
    if (
      typeof window !== 'undefined' &&
      typeof window.localStorage !== 'undefined' &&
      this.localStorageKey
    ) {
      try {
        localStorage.removeItem(this.localStorageKey);
        this.context?.logger.debug(
          'Device ID removed from localStorage (Key: ${this.localStorageKey}).'
        );
      } catch (error) {
        this.context?.logger.error('Error removing device ID from localStorage:', error);
      }
    }
    this.context?.logger.debug('Destroyed. Device ID was: ${this.deviceId}');
    this.deviceId = undefined; // Clear the stored ID from the instance
    // this.localStorageKey is not cleared, in case initialize is called again (though unlikely for a destroyed plugin)
  }
}
